import React, { useCallback, useEffect, useRef, useState } from "react";
import { useVisibleAnimation } from "./useVisibleAnimation";
import { FONT_MONO } from "theme/fonts";
import { localAudio } from "i18n/audio";
import { useSimpleViewText } from "./simpleView.text";

type EyeState = "open" | "half" | "closed" | "dreaming";

interface SpStar {
  x: number;
  y: number;
  r: number;
  tw: number;
  tws: number;
  vx: number;
  vy: number;
  a: number;
  col: string;
}
interface SpDust {
  x: number;
  y: number;
  r: number;
  vx: number;
  vy: number;
  a: number;
  tw: number;
  tws: number;
  col: string;
}
interface SpShoot {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
}
interface OrbPt {
  angle: number;
  radius: number;
  speed: number;
  r: number;
  col: string;
}
interface DebrisPt {
  angle: number;
  speed: number;
  r: number;
  drift: number;
  col: string;
}
interface AccPt {
  angle: number;
  radius: number;
  speed: number;
  tilt: number;
  r: number;
  col: string;
}
interface GalaxyStar {
  r: number;
  theta: number;
  size: number;
  cr: number;
  cg: number;
  cb: number;
}
interface Planet {
  angle: number;
  orbitR: number;
  size: number;
  speed: number;
  type: "ocean" | "gas" | "rocky" | "ringed";
}
interface Pt {
  x: number;
  y: number;
}
interface EyeStar {
  x: number;
  y: number;
  r: number;
  tw: number;
}
interface EyeFiber {
  angle: number;
  len: number;
  alpha: number;
}
interface EyePart {
  angle: number;
  radius: number;
  speed: number;
  r: number;
  alpha: number;
  tw: number;
  tws: number;
  col: string;
}
interface EyeData {
  irisStars: EyeStar[];
  pupilStars: EyeStar[];
  fibers: EyeFiber[];
  particles: EyePart[];
}

// ── Space Background ───────────────────────────────────────────────────────────
const SpaceBackground: React.FC<{ eyeState: EyeState; frozen?: boolean }> = ({ eyeState, frozen }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // FIX: stateRef is updated via a separate effect so the animation loop
  // always reads the current state without needing to re-run setup
  const stateRef = useRef<EyeState>(eyeState);
  const transRef = useRef<number>(0);
  const breathRef = useRef<number>(0);
  const dreamRef = useRef<number>(0);
  // perf: hook-driven loop (pauses offscreen / when hidden, caps fps)
  const drawFnRef = useRef<null | (() => void)>(null);
  const dtRef = useRef<number>(0);
  const frozenRef = useRef(false);
  useEffect(() => {
    frozenRef.current = !!frozen;
  }, [frozen]);

  // FIX: keep stateRef in sync whenever eyeState prop changes
  useEffect(() => {
    stateRef.current = eyeState;
  }, [eyeState]);

  useEffect(() => {
    const canvasOrNull = canvasRef.current;
    if (!canvasOrNull) return;
    const canvas: HTMLCanvasElement = canvasOrNull;
    const ctx = canvas.getContext("2d") as CanvasRenderingContext2D;

    let W = 0,
      H = 0,
      frame = 0;
    let stars: SpStar[] = [];
    let dust: SpDust[] = [];
    let shooting: SpShoot[] = [];
    let orbPts: OrbPt[] = [];
    let debrisPts: DebrisPt[] = [];
    let accPts: AccPt[] = [];
    let galaxyStars: GalaxyStar[] = [];
    let planets: Planet[] = [];

    // FIX: setup reads dimensions from the canvas element's *parent* bounding rect
    // to avoid getting 0×0 when the canvas hasn't painted yet, and also
    // correctly handles resize without leaving a stale W/H in closure
    function setup(): void {
      const parent = canvas.parentElement;
      W = canvas.width = (parent ? parent.clientWidth : canvas.offsetWidth) || 800;
      H = canvas.height = (parent ? parent.clientHeight : canvas.offsetHeight) || 600;

      stars = Array.from(
        { length: 280 },
        (): SpStar => ({
          x: Math.random() * W,
          y: Math.random() * H,
          r: 0.2 + Math.random() * 1.8,
          tw: Math.random() * Math.PI * 2,
          tws: 0.006 + Math.random() * 0.02,
          vx: (Math.random() - 0.5) * 0.08,
          vy: 0.02 + Math.random() * 0.07,
          a: 0.3 + Math.random() * 0.7,
          col: Math.random() < 0.7 ? "255,255,255" : Math.random() < 0.5 ? "255,220,150" : "180,210,255",
        }),
      );
      dust = Array.from(
        { length: 40 },
        (): SpDust => ({
          x: Math.random() * W,
          y: Math.random() * H,
          r: 0.8 + Math.random() * 2.4,
          vx: (Math.random() - 0.5) * 0.4,
          vy: (Math.random() - 0.5) * 0.4,
          a: 0.1 + Math.random() * 0.3,
          tw: Math.random() * Math.PI * 2,
          tws: 0.008 + Math.random() * 0.016,
          col: ["80,160,255", "160,80,255", "40,200,180", "255,155,80"][Math.floor(Math.random() * 4)],
        }),
      );
      orbPts = Array.from(
        { length: 28 },
        (): OrbPt => ({
          angle: Math.random() * Math.PI * 2,
          radius: 55 + Math.random() * 70,
          speed: (0.007 + Math.random() * 0.011) * (Math.random() < 0.5 ? 1 : -1),
          r: 0.8 + Math.random() * 2,
          col: ["255,220,100", "255,180,60", "255,245,180", "255,140,40"][Math.floor(Math.random() * 4)],
        }),
      );
      debrisPts = Array.from(
        { length: 55 },
        (): DebrisPt => ({
          angle: Math.random() * Math.PI * 2,
          speed: 0.5 + Math.random() * 1.8,
          r: 0.6 + Math.random() * 2,
          drift: (Math.random() - 0.5) * 0.08,
          col: ["255,210,120", "180,140,255", "100,200,255", "255,130,60"][Math.floor(Math.random() * 4)],
        }),
      );
      accPts = Array.from(
        { length: 36 },
        (): AccPt => ({
          angle: Math.random() * Math.PI * 2,
          radius: 35 + Math.random() * 80,
          speed: 0.006 + Math.random() * 0.012,
          tilt: 0.18 + Math.random() * 0.1,
          r: 0.6 + Math.random() * 1.6,
          col: ["255,180,80", "255,120,50", "255,200,120", "220,80,50"][Math.floor(Math.random() * 4)],
        }),
      );
      planets = [
        { angle: 0.0, orbitR: 72, size: 7, speed: 0.01, type: "ocean" },
        { angle: 1.8, orbitR: 108, size: 12, speed: 0.007, type: "gas" },
        { angle: 3.5, orbitR: 145, size: 6, speed: 0.005, type: "rocky" },
        { angle: 5.0, orbitR: 182, size: 9, speed: 0.004, type: "ringed" },
      ];
      galaxyStars = [];
      for (let arm = 0; arm < 2; arm++) {
        const armOff = arm * Math.PI;
        for (let i = 0; i < 200; i++) {
          const t = i / 200;
          const theta = t * 2.8 * Math.PI + armOff + (Math.random() - 0.5) * 0.5;
          const cr = t < 0.4 ? Math.round(255 - t * 80) : Math.round(200 - ((t - 0.4) / 0.6) * 150);
          const cg = t < 0.35 ? Math.round(210 + t * 45) : Math.round(255 - ((t - 0.35) / 0.65) * 255);
          const cb = t > 0.3 ? Math.round(((t - 0.3) / 0.7) * 255) : Math.round(80 + t * 80);
          galaxyStars.push({
            r: t,
            theta,
            size: 0.5 + Math.random() * 2 + (t < 0.2 ? 1.5 : 0),
            cr,
            cg,
            cb,
          });
        }
      }
      for (let i = 0; i < 80; i++)
        galaxyStars.push({
          r: 0.3 + Math.random() * 0.7,
          theta: Math.random() * Math.PI * 2,
          size: 0.3 + Math.random() * 0.7,
          cr: 180,
          cg: 150,
          cb: 220,
        });
    }

    function maybeShoot(): void {
      if (shooting.length < 2 && Math.random() < 0.003)
        shooting.push({
          x: Math.random() * W * 0.8,
          y: Math.random() * H * 0.35,
          vx: 10 + Math.random() * 12,
          vy: 2 + Math.random() * 6,
          life: 0,
          maxLife: 30 + Math.random() * 20,
        });
    }

    function drawPlanetAt(
      starX: number,
      starY: number,
      px: number,
      py: number,
      R: number,
      type: "ocean" | "gas" | "rocky" | "ringed",
      alpha: number,
    ): void {
      if (alpha < 0.01) return;
      const ldx = px - starX,
        ldy = py - starY,
        ld = Math.hypot(ldx, ldy) || 1;
      const snx = ldx / ld,
        sny = ldy / ld;
      const litX = px - snx * R * 0.52,
        litY = py - sny * R * 0.52;
      const dkX = px + snx * R * 0.58,
        dkY = py + sny * R * 0.58;
      if (type === "ringed") {
        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.translate(px, py);
        ctx.scale(1, 0.22);
        const innerB = R * 1.35,
          outerB = R * 1.72,
          innerA = R * 1.88,
          outerA = R * 2.8;
        const bg = ctx.createRadialGradient(0, 0, innerB, 0, 0, outerB);
        bg.addColorStop(0, "rgba(195,172,118,0.50)");
        bg.addColorStop(0.3, "rgba(215,192,132,0.85)");
        bg.addColorStop(0.7, "rgba(200,178,122,0.78)");
        bg.addColorStop(1, "rgba(178,155,105,0.50)");
        ctx.beginPath();
        ctx.moveTo(outerB, 0);
        ctx.arc(0, 0, outerB, 0, Math.PI, true);
        ctx.lineTo(-innerB, 0);
        ctx.arc(0, 0, innerB, Math.PI, 0, false);
        ctx.closePath();
        ctx.fillStyle = bg;
        ctx.fill();
        const ag = ctx.createRadialGradient(0, 0, innerA, 0, 0, outerA);
        ag.addColorStop(0, "rgba(185,165,110,0.72)");
        ag.addColorStop(0.5, "rgba(168,148,98,0.48)");
        ag.addColorStop(1, "rgba(0,0,0,0)");
        ctx.beginPath();
        ctx.moveTo(outerA, 0);
        ctx.arc(0, 0, outerA, 0, Math.PI, true);
        ctx.lineTo(-innerA, 0);
        ctx.arc(0, 0, innerA, Math.PI, 0, false);
        ctx.closePath();
        ctx.fillStyle = ag;
        ctx.fill();
        ctx.restore();
      }
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.beginPath();
      ctx.arc(px, py, R, 0, Math.PI * 2);
      ctx.clip();
      if (type === "ocean") {
        ctx.fillStyle = "#1a4a80";
        ctx.beginPath();
        ctx.arc(px, py, R, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = "#4a7a38";
        ctx.beginPath();
        ctx.ellipse(px - R * 0.18, py - R * 0.12, R * 0.38, R * 0.28, 0.4, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = "#5a8840";
        ctx.beginPath();
        ctx.ellipse(px + R * 0.26, py + R * 0.14, R * 0.22, R * 0.17, -0.3, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = "#d8eeff";
        ctx.beginPath();
        ctx.ellipse(px, py - R * 0.84, R * 0.44, R * 0.19, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.ellipse(px, py + R * 0.86, R * 0.36, R * 0.15, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = "rgba(240,248,255,0.32)";
        ctx.beginPath();
        ctx.ellipse(px + R * 0.08, py - R * 0.32, R * 0.54, R * 0.058, 0.2, 0, Math.PI * 2);
        ctx.fill();
      } else if (type === "gas") {
        ctx.fillStyle = "#c07818";
        ctx.beginPath();
        ctx.arc(px, py, R, 0, Math.PI * 2);
        ctx.fill();
        const gasBands = [
          { y: -0.56, h: 0.13, c: "#d48c28" },
          { y: -0.36, h: 0.1, c: "#a86015" },
          { y: -0.18, h: 0.19, c: "#dc9230" },
          { y: 0.1, h: 0.13, c: "#a85e0e" },
          { y: 0.28, h: 0.16, c: "#ca8025" },
          { y: 0.5, h: 0.13, c: "#b47018" },
        ];
        gasBands.forEach((b) => {
          ctx.fillStyle = b.c;
          ctx.beginPath();
          ctx.ellipse(px, py + b.y * R, R, b.h * R, 0, 0, Math.PI * 2);
          ctx.fill();
        });
        ctx.fillStyle = "#c83c1c";
        ctx.beginPath();
        ctx.ellipse(px + R * 0.24, py + R * 0.1, R * 0.28, R * 0.15, 0.1, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = "#e04c2e";
        ctx.beginPath();
        ctx.ellipse(px + R * 0.24, py + R * 0.08, R * 0.17, R * 0.09, 0.1, 0, Math.PI * 2);
        ctx.fill();
      } else if (type === "rocky") {
        ctx.fillStyle = "#782010";
        ctx.beginPath();
        ctx.arc(px, py, R, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = "#a83828";
        ctx.beginPath();
        ctx.ellipse(px - R * 0.14, py + R * 0.1, R * 0.4, R * 0.24, 0.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = "#be5038";
        ctx.beginPath();
        ctx.ellipse(px + R * 0.2, py - R * 0.24, R * 0.27, R * 0.17, -0.3, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = "rgba(195,215,235,0.48)";
        ctx.beginPath();
        ctx.ellipse(px, py - R * 0.83, R * 0.34, R * 0.13, 0, 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.fillStyle = "#d4b870";
        ctx.beginPath();
        ctx.arc(px, py, R, 0, Math.PI * 2);
        ctx.fill();
        const ringBands = [
          { y: -0.4, h: 0.1, c: "rgba(198,172,95,0.60)" },
          { y: -0.18, h: 0.13, c: "rgba(218,192,115,0.40)" },
          { y: 0.12, h: 0.1, c: "rgba(198,172,95,0.50)" },
          { y: 0.38, h: 0.09, c: "rgba(182,158,85,0.45)" },
        ];
        ringBands.forEach((b) => {
          ctx.fillStyle = b.c;
          ctx.beginPath();
          ctx.ellipse(px, py + b.y * R, R, b.h * R, 0, 0, Math.PI * 2);
          ctx.fill();
        });
      }
      const sGrd = ctx.createRadialGradient(dkX, dkY, 0, dkX, dkY, R * 1.95);
      sGrd.addColorStop(0, "rgba(0,0,8,0.92)");
      sGrd.addColorStop(0.35, "rgba(0,0,8,0.68)");
      sGrd.addColorStop(0.65, "rgba(0,0,5,0.22)");
      sGrd.addColorStop(1, "rgba(0,0,0,0)");
      ctx.beginPath();
      ctx.arc(px, py, R, 0, Math.PI * 2);
      ctx.fillStyle = sGrd;
      ctx.fill();
      const lGrd = ctx.createRadialGradient(litX, litY, 0, litX, litY, R * 1.45);
      lGrd.addColorStop(0, "rgba(255,255,230,0.30)");
      lGrd.addColorStop(0.45, "rgba(255,255,200,0.08)");
      lGrd.addColorStop(1, "rgba(0,0,0,0)");
      ctx.beginPath();
      ctx.arc(px, py, R, 0, Math.PI * 2);
      ctx.fillStyle = lGrd;
      ctx.fill();
      ctx.restore();
      ctx.save();
      ctx.globalAlpha = alpha;
      const atmC = type === "ocean" ? "130,180,255" : type === "gas" ? "220,138,58" : type === "rocky" ? "178,98,78" : "198,176,118";
      const atmR = type === "gas" || type === "ringed" ? R * 1.2 : R * 1.14;
      const aGrd = ctx.createRadialGradient(px, py, R * 0.9, px, py, atmR);
      aGrd.addColorStop(0, `rgba(${atmC},${type === "gas" ? 0.58 : 0.36})`);
      aGrd.addColorStop(0.55, `rgba(${atmC},${type === "gas" ? 0.18 : 0.1})`);
      aGrd.addColorStop(1, "rgba(0,0,0,0)");
      ctx.beginPath();
      ctx.arc(px, py, atmR, 0, Math.PI * 2);
      ctx.fillStyle = aGrd;
      ctx.fill();
      ctx.restore();
      if (type === "ringed") {
        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.translate(px, py);
        ctx.scale(1, 0.22);
        const innerB = R * 1.35,
          outerB = R * 1.72,
          innerA = R * 1.88,
          outerA = R * 2.8;
        const bg = ctx.createRadialGradient(0, 0, innerB, 0, 0, outerB);
        bg.addColorStop(0, "rgba(195,172,118,0.50)");
        bg.addColorStop(0.3, "rgba(215,192,132,0.85)");
        bg.addColorStop(0.7, "rgba(200,178,122,0.78)");
        bg.addColorStop(1, "rgba(178,155,105,0.50)");
        ctx.beginPath();
        ctx.moveTo(outerB, 0);
        ctx.arc(0, 0, outerB, 0, Math.PI, false);
        ctx.lineTo(-innerB, 0);
        ctx.arc(0, 0, innerB, Math.PI, 0, true);
        ctx.closePath();
        ctx.fillStyle = bg;
        ctx.fill();
        const ag = ctx.createRadialGradient(0, 0, innerA, 0, 0, outerA);
        ag.addColorStop(0, "rgba(185,165,110,0.72)");
        ag.addColorStop(0.5, "rgba(168,148,98,0.48)");
        ag.addColorStop(1, "rgba(0,0,0,0)");
        ctx.beginPath();
        ctx.moveTo(outerA, 0);
        ctx.arc(0, 0, outerA, 0, Math.PI, false);
        ctx.lineTo(-innerA, 0);
        ctx.arc(0, 0, innerA, Math.PI, 0, true);
        ctx.closePath();
        ctx.fillStyle = ag;
        ctx.fill();
        ctx.restore();
      }
    }

    function drawGalaxy(dreamT: number, CX: number, CY: number): void {
      if (dreamT < 0.01) return;
      const maxGalR = Math.min(W, H) * 0.42;
      const galRot = frame * 0.00055;
      const dbg = ctx.createRadialGradient(CX, CY, 0, CX, CY, Math.max(W, H) * 0.95);
      dbg.addColorStop(0, `rgba(55,12,75,${(dreamT * 0.82).toFixed(3)})`);
      dbg.addColorStop(0.35, `rgba(22,8,48,${(dreamT * 0.62).toFixed(3)})`);
      dbg.addColorStop(0.7, `rgba(8,3,22,${(dreamT * 0.42).toFixed(3)})`);
      dbg.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = dbg;
      ctx.fillRect(0, 0, W, H);
      const galNeb = [
        { dx: -0.3, dy: -0.2, r: 0.24, col: "180,55,225" },
        { dx: 0.26, dy: 0.16, r: 0.22, col: "38,205,180" },
        { dx: -0.1, dy: 0.3, r: 0.2, col: "225,115,55" },
        { dx: 0.22, dy: -0.26, r: 0.18, col: "75,125,255" },
        { dx: -0.26, dy: 0.12, r: 0.16, col: "255,175,75" },
        { dx: 0.12, dy: 0.22, r: 0.15, col: "200,55,200" },
      ];
      galNeb.forEach((n) => {
        const nx = CX + n.dx * maxGalR * 2,
          ny = CY + n.dy * maxGalR * 2 * 0.45,
          nr = n.r * maxGalR;
        const ng = ctx.createRadialGradient(nx, ny, 0, nx, ny, nr);
        ng.addColorStop(0, `rgba(${n.col},${(dreamT * 0.28).toFixed(3)})`);
        ng.addColorStop(0.5, `rgba(${n.col},${(dreamT * 0.1).toFixed(3)})`);
        ng.addColorStop(1, "rgba(0,0,0,0)");
        ctx.beginPath();
        ctx.arc(nx, ny, nr, 0, Math.PI * 2);
        ctx.fillStyle = ng;
        ctx.fill();
      });
      galaxyStars.forEach((s) => {
        const theta = s.theta + galRot;
        const x = CX + maxGalR * s.r * Math.cos(theta);
        const y = CY + maxGalR * s.r * Math.sin(theta) * 0.45;
        const twinkle = 0.35 + 0.65 * Math.abs(Math.sin(frame * 0.018 + s.theta * 3.7));
        const sa = dreamT * twinkle * (s.r < 0.15 ? 0.92 : 0.62);
        ctx.beginPath();
        ctx.arc(x, y, s.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${s.cr},${s.cg},${s.cb},${sa.toFixed(2)})`;
        ctx.fill();
        if (s.size > 1.8) {
          const g = ctx.createRadialGradient(x, y, 0, x, y, s.size * 3.5);
          g.addColorStop(0, `rgba(${s.cr},${s.cg},${s.cb},${(sa * 0.32).toFixed(2)})`);
          g.addColorStop(1, "rgba(0,0,0,0)");
          ctx.beginPath();
          ctx.arc(x, y, s.size * 3.5, 0, Math.PI * 2);
          ctx.fillStyle = g;
          ctx.fill();
        }
      });
      const bulge = ctx.createRadialGradient(CX, CY, 0, CX, CY, maxGalR * 0.22);
      bulge.addColorStop(0, `rgba(255,240,180,${(dreamT * 0.95).toFixed(3)})`);
      bulge.addColorStop(0.2, `rgba(255,200,100,${(dreamT * 0.75).toFixed(3)})`);
      bulge.addColorStop(0.5, `rgba(200,80,255,${(dreamT * 0.35).toFixed(3)})`);
      bulge.addColorStop(0.8, `rgba(80,40,180,${(dreamT * 0.14).toFixed(3)})`);
      bulge.addColorStop(1, "rgba(0,0,0,0)");
      ctx.beginPath();
      ctx.arc(CX, CY, maxGalR * 0.22, 0, Math.PI * 2);
      ctx.fillStyle = bulge;
      ctx.fill();
      const core = ctx.createRadialGradient(CX, CY, 0, CX, CY, maxGalR * 0.06);
      core.addColorStop(0, `rgba(255,255,255,${(dreamT * 0.98).toFixed(3)})`);
      core.addColorStop(0.4, `rgba(255,220,150,${(dreamT * 0.72).toFixed(3)})`);
      core.addColorStop(1, "rgba(0,0,0,0)");
      ctx.beginPath();
      ctx.arc(CX, CY, maxGalR * 0.06, 0, Math.PI * 2);
      ctx.fillStyle = core;
      ctx.fill();
    }

    function draw(): void {
      ctx.clearRect(0, 0, W, H);
      const tgt = stateRef.current === "open" ? 0 : stateRef.current === "half" ? 0.5 : 1.0;
      transRef.current += (tgt - transRef.current) * 0.009;
      const T = transRef.current;
      const dreamTgt = stateRef.current === "dreaming" ? 1.0 : 0.0;
      dreamRef.current += (dreamTgt - dreamRef.current) * 0.01;
      const dreamT = dreamRef.current;
      const starA = T <= 0.5 ? 1 - T * 1.8 : 0;
      const novaA = T <= 0.5 ? T * 2 : Math.max(0, 1 - (T - 0.5) * 3.2);
      const bhA = T >= 0.5 ? Math.min(1, (T - 0.5) * 2) : 0;
      const effectiveBhA = bhA * (1 - dreamT * 0.85);
      breathRef.current += 0.028;
      const pulse = 0.5 + 0.5 * Math.sin(breathRef.current);

      const CX = W / 2,
        CY = H * 0.2;

      const bgR = T <= 0.5 ? Math.round(9 + T * 2 * 16) : Math.round(25 - (T - 0.5) * 2 * 22);
      const bgG = T <= 0.5 ? Math.round(9 + T * 2 * 4) : Math.round(13 - (T - 0.5) * 2 * 10);
      const bgB = T <= 0.5 ? Math.round(32 - T * 2 * 8) : Math.round(24 - (T - 0.5) * 2 * 18);
      const bg = ctx.createRadialGradient(CX, CY, 0, CX, CY, Math.max(W, H) * 1.1);
      bg.addColorStop(0, `rgb(${bgR},${bgG},${bgB})`);
      bg.addColorStop(0.4, `rgb(${Math.round(bgR * 0.5)},${Math.round(bgG * 0.5)},${Math.round(bgB * 0.6)})`);
      bg.addColorStop(1, "rgb(1,1,8)");
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, W, H);

      dust.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < -30) p.x = W + 30;
        if (p.x > W + 30) p.x = -30;
        if (p.y < -30) p.y = H + 30;
        if (p.y > H + 30) p.y = -30;
        const tw = 0.3 + 0.7 * Math.abs(Math.sin(frame * p.tws + p.tw)),
          a = (p.a * tw).toFixed(2);
        const gr = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.r * 5);
        gr.addColorStop(0, `rgba(${p.col},${a})`);
        gr.addColorStop(1, "rgba(0,0,0,0)");
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r * 5, 0, Math.PI * 2);
        ctx.fillStyle = gr;
        ctx.fill();
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${p.col},${Math.min(1, p.a * tw * 1.5).toFixed(2)})`;
        ctx.fill();
      });

      stars.forEach((s) => {
        s.x += s.vx;
        s.y += s.vy;
        if (s.y > H) {
          s.y = 0;
          s.x = Math.random() * W;
        }
        if (s.x < 0) s.x = W;
        if (s.x > W) s.x = 0;
        const tw = 0.3 + 0.7 * Math.abs(Math.sin(frame * s.tws + s.tw));
        const sa = tw * s.a * (effectiveBhA > 0 ? Math.max(0.2, 1 - effectiveBhA * 0.5) : 1) * (1 - dreamT * 0.3);
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${s.col},${sa.toFixed(2)})`;
        ctx.fill();
      });

      const pAlpha = T <= 0.5 ? Math.max(0, 1 - T * 2.8) : 0;
      const pScatter = T >= 0.15 ? Math.min(1, (T - 0.15) / 0.35) : 0;
      planets.forEach((p) => {
        p.angle += p.speed;
      });

      // ═══ STAR ═══
      if (starA > 0.01) {
        const rayRot = frame * 0.0045;
        for (let i = 0; i < 12; i++) {
          const a = (i / 12) * Math.PI * 2 + rayRot,
            isLong = i % 3 === 0;
          const rl = (isLong ? 200 : 115) * (0.9 + 0.2 * pulse),
            rw = (isLong ? 16 : 9) * (0.9 + 0.15 * pulse);
          const ex = CX + rl * Math.cos(a),
            ey = CY + rl * Math.sin(a);
          const rg = ctx.createLinearGradient(CX, CY, ex, ey);
          const ra = (isLong ? 0.5 : 0.28) * starA;
          rg.addColorStop(0, `rgba(255,245,170,${ra})`);
          rg.addColorStop(0.25, `rgba(255,200,80,${ra * 0.7})`);
          rg.addColorStop(0.65, `rgba(255,120,30,${ra * 0.3})`);
          rg.addColorStop(1, "rgba(255,60,0,0)");
          const nx = -Math.sin(a) * rw,
            ny = Math.cos(a) * rw;
          ctx.beginPath();
          ctx.moveTo(CX + nx, CY + ny);
          ctx.lineTo(ex, ey);
          ctx.lineTo(CX - nx, CY - ny);
          ctx.closePath();
          ctx.fillStyle = rg;
          ctx.fill();
        }
        const cR = 42 + 14 * pulse;
        const corona = ctx.createRadialGradient(CX, CY, cR * 0.25, CX, CY, cR * 5);
        corona.addColorStop(0, `rgba(255,245,160,${0.75 * starA})`);
        corona.addColorStop(0.25, `rgba(255,200,60,${0.4 * starA})`);
        corona.addColorStop(0.6, `rgba(220,80,20,${0.15 * starA})`);
        corona.addColorStop(1, "rgba(0,0,0,0)");
        ctx.beginPath();
        ctx.arc(CX, CY, cR * 5, 0, Math.PI * 2);
        ctx.fillStyle = corona;
        ctx.fill();
        const coreG = ctx.createRadialGradient(CX, CY, 0, CX, CY, cR);
        coreG.addColorStop(0, `rgba(255,255,255,${starA})`);
        coreG.addColorStop(0.35, `rgba(255,255,200,${0.95 * starA})`);
        coreG.addColorStop(0.7, `rgba(255,190,60,${0.7 * starA})`);
        coreG.addColorStop(1, "rgba(255,80,0,0)");
        ctx.beginPath();
        ctx.arc(CX, CY, cR, 0, Math.PI * 2);
        ctx.fillStyle = coreG;
        ctx.fill();
      }

      // ═══ PLANETS ═══
      if (pAlpha > 0.01) {
        if (pScatter < 0.12) {
          planets.forEach((p) => {
            const oa = pAlpha * (1 - pScatter * 8) * 0.1;
            if (oa < 0.01) return;
            ctx.beginPath();
            ctx.ellipse(CX, CY, p.orbitR, p.orbitR * 0.3, 0, 0, Math.PI * 2);
            ctx.strokeStyle = `rgba(255,220,100,${oa.toFixed(3)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          });
        }
        [...planets]
          .sort((a, b) => Math.sin(a.angle) - Math.sin(b.angle))
          .forEach((p) => {
            const bx = CX + p.orbitR * Math.cos(p.angle),
              by = CY + p.orbitR * Math.sin(p.angle) * 0.3;
            const x = CX + (bx - CX) * (1 + pScatter * 3.2),
              y = CY + (by - CY) * (1 + pScatter * 3.2);
            const depth = Math.sin(p.angle),
              depthScale = 1 + depth * 0.18;
            drawPlanetAt(CX, CY, x, y, p.size * depthScale, p.type, pAlpha);
          });
      }

      // ═══ SUPERNOVA ═══
      if (novaA > 0.01) {
        const maxRingR = Math.min(W, H) * 0.44;
        for (let w = 0; w < 3; w++) {
          const offset = w * (88 / 3),
            phase = ((frame + offset) % 88) / 88;
          const ringR = maxRingR * phase,
            ringOp = novaA * (1 - phase) * (1 - phase) * 0.88;
          if (ringOp < 0.005) continue;
          const rc = phase < 0.55 ? 255 : Math.round(255 - ((phase - 0.55) / 0.45) * 155);
          const gc = phase < 0.35 ? Math.round(200 + phase * 155) : Math.round(255 - ((phase - 0.35) / 0.65) * 255);
          const bc = phase > 0.45 ? Math.round(((phase - 0.45) / 0.55) * 220) : 30;
          ctx.beginPath();
          ctx.arc(CX, CY, ringR, 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(${rc},${gc},${bc},${ringOp.toFixed(3)})`;
          ctx.lineWidth = 1.5 + (1 - phase) * 5.5;
          ctx.stroke();
          const rg = ctx.createRadialGradient(CX, CY, Math.max(0, ringR - 22), CX, CY, ringR + 22);
          rg.addColorStop(0, "rgba(0,0,0,0)");
          rg.addColorStop(0.5, `rgba(${rc},${gc},${bc},${(ringOp * 0.22).toFixed(3)})`);
          rg.addColorStop(1, "rgba(0,0,0,0)");
          ctx.beginPath();
          ctx.arc(CX, CY, ringR + 22, 0, Math.PI * 2);
          ctx.fillStyle = rg;
          ctx.fill();
        }
        const flashR = (28 + 12 * pulse) * novaA;
        const flash = ctx.createRadialGradient(CX, CY, 0, CX, CY, flashR);
        flash.addColorStop(0, `rgba(255,255,255,${(novaA * 0.95).toFixed(3)})`);
        flash.addColorStop(0.3, `rgba(255,235,130,${(novaA * 0.7).toFixed(3)})`);
        flash.addColorStop(0.65, `rgba(200,100,255,${(novaA * 0.35).toFixed(3)})`);
        flash.addColorStop(1, "rgba(0,0,0,0)");
        ctx.beginPath();
        ctx.arc(CX, CY, flashR, 0, Math.PI * 2);
        ctx.fillStyle = flash;
        ctx.fill();
      }

      // ═══ BLACK HOLE ═══
      if (effectiveBhA > 0.01) {
        const bhR = Math.min(W, H) * 0.055 * Math.min(1, effectiveBhA * 1.3);
        const diskPulse = 0.5 + 0.5 * Math.sin(frame * 0.016);
        const farGlow = ctx.createRadialGradient(CX, CY, bhR * 1.8, CX, CY, bhR * 8);
        farGlow.addColorStop(0, `rgba(130,40,220,${(effectiveBhA * 0.55).toFixed(3)})`);
        farGlow.addColorStop(0.35, `rgba(70,15,160,${(effectiveBhA * 0.28).toFixed(3)})`);
        farGlow.addColorStop(0.75, `rgba(30,5,80,${(effectiveBhA * 0.12).toFixed(3)})`);
        farGlow.addColorStop(1, "rgba(0,0,0,0)");
        ctx.beginPath();
        ctx.arc(CX, CY, bhR * 8, 0, Math.PI * 2);
        ctx.fillStyle = farGlow;
        ctx.fill();
        accPts.forEach((p) => {
          p.angle += p.speed * (1 + 0.5 * (1 - p.radius / (bhR * 3)));
          p.radius = Math.max(bhR * 1.15, p.radius - 0.08);
          if (p.radius <= bhR * 1.2) p.radius = bhR * 1.2 + Math.random() * bhR * 2.5;
          const x = CX + p.radius * Math.cos(p.angle),
            y = CY + p.radius * Math.sin(p.angle) * p.tilt;
          const a = effectiveBhA * (0.7 + 0.3 * diskPulse);
          const g = ctx.createRadialGradient(x, y, 0, x, y, p.r * 3);
          g.addColorStop(0, `rgba(${p.col},${a.toFixed(2)})`);
          g.addColorStop(1, "rgba(0,0,0,0)");
          ctx.beginPath();
          ctx.arc(x, y, p.r * 3, 0, Math.PI * 2);
          ctx.fillStyle = g;
          ctx.fill();
        });
        ctx.save();
        ctx.translate(CX, CY);
        ctx.scale(1, 0.18);
        for (let ring = 0; ring < 3; ring++) {
          const rin = bhR * (1.15 + ring * 0.45),
            rout = rin + bhR * (0.7 - ring * 0.15);
          const da = effectiveBhA * (0.9 - ring * 0.2) * (0.85 + 0.15 * diskPulse);
          const diskG = ctx.createRadialGradient(0, 0, rin, 0, 0, rout);
          if (ring === 0) {
            diskG.addColorStop(0, `rgba(255,255,200,${da.toFixed(3)})`);
            diskG.addColorStop(0.4, `rgba(255,180,60,${(da * 0.8).toFixed(3)})`);
            diskG.addColorStop(1, "rgba(255,80,20,0)");
          } else if (ring === 1) {
            diskG.addColorStop(0, `rgba(255,160,40,${(da * 0.7).toFixed(3)})`);
            diskG.addColorStop(0.5, `rgba(220,80,30,${(da * 0.4).toFixed(3)})`);
            diskG.addColorStop(1, "rgba(0,0,0,0)");
          } else {
            diskG.addColorStop(0, `rgba(200,60,20,${(da * 0.4).toFixed(3)})`);
            diskG.addColorStop(0.6, `rgba(150,30,80,${(da * 0.2).toFixed(3)})`);
            diskG.addColorStop(1, "rgba(0,0,0,0)");
          }
          ctx.beginPath();
          ctx.arc(0, 0, rout, 0, Math.PI * 2);
          ctx.fillStyle = diskG;
          ctx.fill();
        }
        ctx.restore();
        const jetLen = Math.min(W, H) * 0.38 * effectiveBhA,
          jetW = bhR * 0.32;
        (
          [
            [CY, CY - jetLen],
            [CY, CY + jetLen],
          ] as [number, number][]
        ).forEach(([y0, y1]) => {
          const jg = ctx.createLinearGradient(CX, y0, CX, y1);
          jg.addColorStop(0, `rgba(200,230,255,${(effectiveBhA * 0.85).toFixed(2)})`);
          jg.addColorStop(0.25, `rgba(140,180,255,${(effectiveBhA * 0.55).toFixed(2)})`);
          jg.addColorStop(0.6, `rgba(80,120,255,${(effectiveBhA * 0.25).toFixed(2)})`);
          jg.addColorStop(1, "rgba(0,0,0,0)");
          ctx.beginPath();
          ctx.moveTo(CX - jetW, y0);
          ctx.lineTo(CX, y1);
          ctx.lineTo(CX + jetW, y0);
          ctx.closePath();
          ctx.fillStyle = jg;
          ctx.fill();
        });
        const photon = ctx.createRadialGradient(CX, CY, bhR * 0.9, CX, CY, bhR * 1.7);
        photon.addColorStop(0, `rgba(255,220,120,${(effectiveBhA * 0.95).toFixed(3)})`);
        photon.addColorStop(0.5, `rgba(255,140,60,${(effectiveBhA * 0.55).toFixed(3)})`);
        photon.addColorStop(1, "rgba(0,0,0,0)");
        ctx.beginPath();
        ctx.arc(CX, CY, bhR * 1.7, 0, Math.PI * 2);
        ctx.fillStyle = photon;
        ctx.fill();
        ctx.beginPath();
        ctx.arc(CX, CY, bhR, 0, Math.PI * 2);
        ctx.fillStyle = "#000000";
        ctx.fill();
      }

      drawGalaxy(dreamT, CX, CY);

      maybeShoot();
      shooting = shooting.filter((s) => s.life < s.maxLife);
      shooting.forEach((s) => {
        s.x += s.vx;
        s.y += s.vy;
        s.life++;
        const prog = s.life / s.maxLife,
          alpha = prog < 0.25 ? prog / 0.25 : 1 - (prog - 0.25) / 0.75;
        const gr = ctx.createLinearGradient(s.x - s.vx * 10, s.y - s.vy * 10, s.x, s.y);
        gr.addColorStop(0, "rgba(255,255,255,0)");
        gr.addColorStop(0.6, `rgba(210,235,255,${(alpha * 0.5).toFixed(2)})`);
        gr.addColorStop(1, `rgba(255,255,255,${alpha.toFixed(2)})`);
        ctx.beginPath();
        ctx.moveTo(s.x - s.vx * 10, s.y - s.vy * 10);
        ctx.lineTo(s.x, s.y);
        ctx.strokeStyle = gr;
        ctx.lineWidth = 1.6;
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(s.x, s.y, 2, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${alpha.toFixed(2)})`;
        ctx.fill();
      });

      frame += dtRef.current > 0 ? dtRef.current * 60 : 1;
    }

    // FIX: use requestAnimationFrame for first setup so DOM has painted
    // and clientWidth/clientHeight are available
    let setupRafId = requestAnimationFrame(() => {
      setup();
      drawFnRef.current = draw;
    });

    // FIX: debounced resize — calls setup() then lets the existing RAF loop
    // pick up new W/H on the very next draw() call
    let resizeTimer: ReturnType<typeof setTimeout>;
    const onResize = (): void => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => setup(), 150);
    };
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(setupRafId);
      clearTimeout(resizeTimer);
      window.removeEventListener("resize", onResize);
      drawFnRef.current = null;
    };
    // FIX: empty dep array — runs once, stateRef handles eyeState changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useVisibleAnimation(
    canvasRef,
    (dt) => {
      if (frozenRef.current) return;
      dtRef.current = dt;
      drawFnRef.current?.();
    },
    { fps: 30 },
  );

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        display: "block",
      }}
    />
  );
};

// ── Dual Eye Canvas ────────────────────────────────────────────────────────────
const DualEyeCanvas: React.FC<{
  eyeState: EyeState;
  frozen?: boolean;
  armed?: boolean;
  onSettled?: () => void;
}> = ({ eyeState, frozen, armed, onSettled }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const stateRef = useRef<EyeState>(eyeState);
  const blinkRef = useRef<number>(1.0);
  const dreamRef = useRef<number>(0);
  const settledRef = useRef(false);
  const armedRef = useRef(false);
  useEffect(() => {
    armedRef.current = !!armed;
    if (!armed) settledRef.current = false;
  }, [armed]);
  const onSettledRef = useRef(onSettled);
  useEffect(() => {
    onSettledRef.current = onSettled;
  }, [onSettled]);
  // perf: hook-driven loop (pauses offscreen / when hidden, caps fps)
  const drawFnRef = useRef<null | (() => void)>(null);
  const dtRef = useRef<number>(0);
  const frozenRef = useRef(false);
  useEffect(() => {
    frozenRef.current = !!frozen;
  }, [frozen]);

  // FIX: keep stateRef current so the animation loop reads latest state
  useEffect(() => {
    stateRef.current = eyeState;
  }, [eyeState]);

  useEffect(() => {
    const canvasOrNull = canvasRef.current;
    if (!canvasOrNull) return;
    const canvas: HTMLCanvasElement = canvasOrNull;
    const ctx = canvas.getContext("2d") as CanvasRenderingContext2D;
    const W = 840,
      H = 460;
    canvas.width = W;
    canvas.height = H;
    const ECY = 248,
      IRIS_R = 82,
      PUPIL_R_BASE = 40;
    const ECX1 = 210,
      ELX1 = 28,
      ERX1 = 392;
    const TC1B1: Pt = { x: 118, y: 100 },
      TC2B1: Pt = { x: 302, y: 100 };
    const BC1B1: Pt = { x: 118, y: 378 },
      BC2B1: Pt = { x: 302, y: 378 };
    const SH = 420;
    const ECX2 = ECX1 + SH,
      ELX2 = ELX1 + SH,
      ERX2 = ERX1 + SH;
    const TC1B2: Pt = { x: TC1B1.x + SH, y: TC1B1.y },
      TC2B2: Pt = { x: TC2B1.x + SH, y: TC2B1.y };
    const BC1B2: Pt = { x: BC1B1.x + SH, y: BC1B1.y },
      BC2B2: Pt = { x: BC2B1.x + SH, y: BC2B1.y };

    function makeEyeData(ecx: number): EyeData {
      return {
        irisStars: Array.from({ length: 70 }, (): EyeStar => {
          const a = Math.random() * Math.PI * 2,
            r = PUPIL_R_BASE + 8 + Math.random() * (IRIS_R - PUPIL_R_BASE - 10);
          return {
            x: ecx + r * Math.cos(a),
            y: ECY + r * Math.sin(a),
            r: 0.6 + Math.random() * 2.2,
            tw: Math.random() * Math.PI * 2,
          };
        }),
        pupilStars: Array.from({ length: 28 }, (): EyeStar => {
          const a = Math.random() * Math.PI * 2,
            r = 3 + Math.random() * (PUPIL_R_BASE - 7);
          return {
            x: ecx + r * Math.cos(a),
            y: ECY + r * Math.sin(a),
            r: 0.6 + Math.random() * 1.6,
            tw: Math.random() * Math.PI * 2,
          };
        }),
        fibers: Array.from(
          { length: 52 },
          (_, i): EyeFiber => ({
            angle: (i / 52) * Math.PI * 2 + (Math.random() - 0.5) * 0.1,
            len: 0.4 + Math.random() * 0.55,
            alpha: 0.18 + Math.random() * 0.32,
          }),
        ),
        particles: Array.from(
          { length: 45 },
          (): EyePart => ({
            angle: Math.random() * Math.PI * 2,
            radius: IRIS_R + 35 + Math.random() * 130,
            speed: (0.002 + Math.random() * 0.005) * (Math.random() < 0.5 ? 1 : -1),
            r: 0.6 + Math.random() * 2.2,
            alpha: 0.35 + Math.random() * 0.65,
            tw: Math.random() * Math.PI * 2,
            tws: 0.018 + Math.random() * 0.038,
            col: ["255,255,255", "120,200,255", "200,150,255", "80,220,200"][Math.floor(Math.random() * 4)],
          }),
        ),
      };
    }

    const eye1 = makeEyeData(ECX1),
      eye2 = makeEyeData(ECX2);
    let frame = 0;

    function buildEyePath(elx: number, erx: number, tc1: Pt, tc2: Pt, bc1: Pt, bc2: Pt): void {
      ctx.beginPath();
      ctx.moveTo(elx, ECY);
      ctx.bezierCurveTo(tc1.x, tc1.y, tc2.x, tc2.y, erx, ECY);
      ctx.bezierCurveTo(bc1.x, bc1.y, bc2.x, bc2.y, elx, ECY);
      ctx.closePath();
    }
    function upperPt(t: number, elx: number, erx: number, tc1: Pt, tc2: Pt): Pt {
      const m = 1 - t;
      return {
        x: m * m * m * elx + 3 * m * m * t * tc1.x + 3 * m * t * t * tc2.x + t * t * t * erx,
        y: m * m * m * ECY + 3 * m * m * t * tc1.y + 3 * m * t * t * tc2.y + t * t * t * ECY,
      };
    }

    function drawEye(
      ecx: number,
      elx: number,
      erx: number,
      tc1b: Pt,
      tc2b: Pt,
      bc1b: Pt,
      bc2b: Pt,
      data: EyeData,
      pulse: number,
      slowPulse: number,
      rot: number,
      blink: number,
      PUPIL_R: number,
      dreamFactor: number,
    ): void {
      const TC1: Pt = {
        x: tc1b.x,
        y: tc1b.y + (bc1b.y - tc1b.y) * (1 - blink),
      };
      const TC2: Pt = {
        x: tc2b.x,
        y: tc2b.y + (bc2b.y - tc2b.y) * (1 - blink),
      };
      const BC1: Pt = { x: bc1b.x, y: bc1b.y },
        BC2: Pt = { x: bc2b.x, y: bc2b.y };

      data.particles.forEach((p) => {
        p.angle += p.speed;
        const x = ecx + p.radius * Math.cos(p.angle),
          y = ECY + p.radius * Math.sin(p.angle) * 0.55;
        const tw = 0.35 + 0.65 * Math.abs(Math.sin(frame * p.tws + p.tw)),
          a = p.alpha * tw;
        const g = ctx.createRadialGradient(x, y, 0, x, y, p.r * 4);
        g.addColorStop(0, `rgba(${p.col},${a})`);
        g.addColorStop(1, "rgba(0,0,0,0)");
        ctx.beginPath();
        ctx.arc(x, y, p.r * 4, 0, Math.PI * 2);
        ctx.fillStyle = g;
        ctx.fill();
        ctx.beginPath();
        ctx.arc(x, y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${p.col},${a})`;
        ctx.fill();
      });

      for (let i = 0; i < 16; i++) {
        const angle = (i / 16) * Math.PI * 2 + rot * 0.08,
          isLong = i % 4 === 0,
          isMed = i % 4 === 1,
          isShort = i % 4 === 2;
        const rayLen = isLong ? 260 : isMed ? 180 : isShort ? 120 : 80;
        const rayBase = isLong ? 28 : isMed ? 18 : isShort ? 12 : 7;
        const ex = ecx + rayLen * Math.cos(angle),
          ey = ECY + rayLen * Math.sin(angle);
        const peakA =
          (isLong ? 0.38 + 0.12 * pulse : isMed ? 0.22 + 0.08 * pulse : isShort ? 0.13 + 0.05 * pulse : 0.07 + 0.03 * pulse) * blink;
        const rg = ctx.createLinearGradient(ecx, ECY, ex, ey);
        rg.addColorStop(0, `rgba(200,235,255,${peakA})`);
        rg.addColorStop(0.2, `rgba(100,180,255,${peakA * 0.7})`);
        rg.addColorStop(0.55, `rgba(80,60,220,${peakA * 0.28})`);
        rg.addColorStop(1, "rgba(55,138,221,0)");
        const px2 = ecx - Math.sin(angle) * rayBase,
          py2 = ECY + Math.cos(angle) * rayBase;
        const px3 = ecx + Math.sin(angle) * rayBase,
          py3 = ECY - Math.cos(angle) * rayBase;
        ctx.beginPath();
        ctx.moveTo(px2, py2);
        ctx.lineTo(ex, ey);
        ctx.lineTo(px3, py3);
        ctx.closePath();
        ctx.fillStyle = rg;
        ctx.fill();
      }

      const og = ctx.createRadialGradient(ecx, ECY, IRIS_R * 0.65, ecx, ECY, IRIS_R + 150);
      og.addColorStop(0, `rgba(120,200,255,${(0.85 + 0.15 * pulse) * blink})`);
      og.addColorStop(0.2, `rgba(70,130,250,${(0.55 + 0.18 * pulse) * blink})`);
      og.addColorStop(0.55, `rgba(110,40,210,${(0.25 + 0.08 * pulse) * blink})`);
      og.addColorStop(1, "rgba(55,138,221,0)");
      ctx.beginPath();
      ctx.ellipse(ecx, ECY, IRIS_R + 150, IRIS_R + 115, 0, 0, Math.PI * 2);
      ctx.fillStyle = og;
      ctx.fill();

      const bl = ctx.createRadialGradient(ecx, ECY, IRIS_R * 0.5, ecx, ECY, IRIS_R + 80);
      bl.addColorStop(0, `rgba(220,80,255,${(0.22 + 0.1 * slowPulse) * blink})`);
      bl.addColorStop(0.5, `rgba(80,40,200,${(0.12 + 0.05 * slowPulse) * blink})`);
      bl.addColorStop(1, "rgba(0,0,0,0)");
      ctx.beginPath();
      ctx.ellipse(ecx, ECY, IRIS_R + 80, IRIS_R + 60, 0, 0, Math.PI * 2);
      ctx.fillStyle = bl;
      ctx.fill();

      ctx.save();
      buildEyePath(elx, erx, TC1, TC2, BC1, BC2);
      ctx.clip();
      buildEyePath(elx, erx, TC1, TC2, BC1, BC2);
      const sb = ctx.createRadialGradient(ecx, ECY - 20, 0, ecx, ECY, 195);
      sb.addColorStop(0, "#0c061e");
      sb.addColorStop(0.55, "#07031a");
      sb.addColorStop(1, "#020112");
      ctx.fillStyle = sb;
      ctx.fill();

      const eyeNeb = [
        { x: ecx - 30, y: ECY - 25, r: 115, c: "rgba(130,25,230,0.65)" },
        { x: ecx + 40, y: ECY + 15, r: 100, c: "rgba(15,100,230,0.58)" },
        { x: ecx - 10, y: ECY + 35, r: 85, c: "rgba(0,200,250,0.45)" },
        { x: ecx + 22, y: ECY - 40, r: 80, c: "rgba(200,35,250,0.42)" },
        { x: ecx, y: ECY, r: 70, c: "rgba(50,150,255,0.38)" },
        { x: ecx - 55, y: ECY + 12, r: 60, c: "rgba(0,240,190,0.28)" },
        { x: ecx + 50, y: ECY - 20, r: 55, c: "rgba(255,80,180,0.18)" },
      ];
      eyeNeb.forEach((n) => {
        const g = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, n.r);
        g.addColorStop(0, n.c);
        g.addColorStop(1, "rgba(0,0,0,0)");
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx.fillStyle = g;
        ctx.fill();
      });

      const ir = ctx.createRadialGradient(ecx, ECY, IRIS_R * 0.78, ecx, ECY, IRIS_R);
      ir.addColorStop(0, "rgba(55,138,221,0)");
      ir.addColorStop(0.45, `rgba(120,210,255,${0.52 + 0.2 * pulse})`);
      ir.addColorStop(1, `rgba(200,248,255,${0.88 + 0.12 * pulse})`);
      ctx.beginPath();
      ctx.arc(ecx, ECY, IRIS_R, 0, Math.PI * 2);
      ctx.fillStyle = ir;
      ctx.fill();

      ctx.save();
      ctx.translate(ecx, ECY);
      ctx.rotate(rot);
      ctx.beginPath();
      ctx.arc(0, 0, IRIS_R, 0, Math.PI * 2);
      ctx.clip();
      for (let i = 0; i < 28; i++) {
        const a1 = (i / 28) * Math.PI * 2,
          a2 = ((i + 0.65) / 28) * Math.PI * 2;
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.arc(0, 0, IRIS_R - 1, a1, a2);
        ctx.closePath();
        const c = ["rgba(55,138,221,", "rgba(130,45,245,", "rgba(0,190,250,", "rgba(200,55,230,"][i % 4];
        ctx.fillStyle = `${c}${0.12 + (i % 3) * 0.07})`;
        ctx.fill();
      }
      ctx.restore();

      if (dreamFactor > 0.01) {
        ctx.save();
        ctx.globalAlpha = Math.min(1, dreamFactor * 1.1);
        ctx.translate(ecx, ECY);
        ctx.rotate(-rot * 0.5);
        ctx.beginPath();
        ctx.arc(0, 0, IRIS_R, 0, Math.PI * 2);
        ctx.clip();
        const dp = ["rgba(255,180,40,", "rgba(55,220,180,", "rgba(200,55,255,", "rgba(255,120,60,"];
        for (let i = 0; i < 28; i++) {
          const a1 = (i / 28) * Math.PI * 2,
            a2 = ((i + 0.65) / 28) * Math.PI * 2;
          ctx.beginPath();
          ctx.moveTo(0, 0);
          ctx.arc(0, 0, IRIS_R - 1, a1, a2);
          ctx.closePath();
          ctx.fillStyle = `${dp[i % 4]}${0.12 + (i % 3) * 0.07})`;
          ctx.fill();
        }
        ctx.restore();
      }

      ctx.save();
      ctx.translate(ecx, ECY);
      ctx.beginPath();
      ctx.arc(0, 0, IRIS_R, 0, Math.PI * 2);
      ctx.clip();
      const sa = frame * 0.028;
      const sg = ctx.createLinearGradient(
        Math.cos(sa) * IRIS_R,
        Math.sin(sa) * IRIS_R,
        Math.cos(sa + Math.PI) * IRIS_R,
        Math.sin(sa + Math.PI) * IRIS_R,
      );
      sg.addColorStop(0, "rgba(220,248,255,0)");
      sg.addColorStop(0.44, "rgba(220,248,255,0)");
      sg.addColorStop(0.5, `rgba(220,248,255,${0.22 + 0.1 * pulse})`);
      sg.addColorStop(0.56, "rgba(220,248,255,0)");
      sg.addColorStop(1, "rgba(220,248,255,0)");
      ctx.fillStyle = sg;
      ctx.fillRect(-IRIS_R, -IRIS_R, IRIS_R * 2, IRIS_R * 2);
      ctx.restore();

      if (dreamFactor > 0.01) {
        ctx.save();
        ctx.translate(ecx, ECY);
        ctx.beginPath();
        ctx.arc(0, 0, IRIS_R, 0, Math.PI * 2);
        ctx.clip();
        const da = frame * 0.035;
        const dsg = ctx.createLinearGradient(
          Math.cos(da + 1.0) * IRIS_R,
          Math.sin(da + 1.0) * IRIS_R,
          Math.cos(da + 1.0 + Math.PI) * IRIS_R,
          Math.sin(da + 1.0 + Math.PI) * IRIS_R,
        );
        const dA = dreamFactor * (0.2 + 0.1 * pulse);
        dsg.addColorStop(0, "rgba(255,200,80,0)");
        dsg.addColorStop(0.44, "rgba(255,200,80,0)");
        dsg.addColorStop(0.5, `rgba(255,200,80,${dA})`);
        dsg.addColorStop(0.56, "rgba(255,200,80,0)");
        dsg.addColorStop(1, "rgba(255,200,80,0)");
        ctx.fillStyle = dsg;
        ctx.fillRect(-IRIS_R, -IRIS_R, IRIS_R * 2, IRIS_R * 2);
        ctx.restore();
      }

      ctx.save();
      ctx.translate(ecx, ECY);
      ctx.rotate(-rot * 0.6);
      ctx.beginPath();
      ctx.arc(0, 0, IRIS_R, 0, Math.PI * 2);
      ctx.clip();
      data.fibers.forEach((f) => {
        const x1 = Math.cos(f.angle) * PUPIL_R_BASE,
          y1 = Math.sin(f.angle) * PUPIL_R_BASE;
        const x2 = Math.cos(f.angle) * (PUPIL_R_BASE + (IRIS_R - PUPIL_R_BASE) * f.len),
          y2 = Math.sin(f.angle) * (PUPIL_R_BASE + (IRIS_R - PUPIL_R_BASE) * f.len);
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.strokeStyle = `rgba(180,228,255,${f.alpha})`;
        ctx.lineWidth = 0.9;
        ctx.stroke();
      });
      ctx.restore();

      const ig = ctx.createRadialGradient(ecx, ECY, PUPIL_R_BASE, ecx, ECY, IRIS_R);
      ig.addColorStop(0, `rgba(110,210,255,${0.42 + 0.16 * pulse})`);
      ig.addColorStop(1, "rgba(80,180,255,0)");
      ctx.beginPath();
      ctx.arc(ecx, ECY, IRIS_R, 0, Math.PI * 2);
      ctx.fillStyle = ig;
      ctx.fill();

      ctx.beginPath();
      ctx.arc(ecx, ECY, IRIS_R, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(140,235,255,${0.45 + 0.14 * pulse})`;
      ctx.lineWidth = 9;
      ctx.stroke();
      ctx.strokeStyle = `rgba(200,248,255,${0.92 + 0.08 * pulse})`;
      ctx.lineWidth = 3;
      ctx.stroke();

      data.irisStars.forEach((s) => {
        const d = Math.hypot(s.x - ecx, s.y - ECY);
        if (d > PUPIL_R_BASE + 2 && d < IRIS_R - 2) {
          const tw = 0.3 + 0.7 * Math.abs(Math.sin(frame * 0.028 + s.tw));
          ctx.beginPath();
          ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(255,255,255,${(tw * 0.95).toFixed(2)})`;
          ctx.fill();
        }
      });

      const pg = ctx.createRadialGradient(ecx - 9, ECY - 9, 0, ecx, ECY, PUPIL_R);
      pg.addColorStop(0, "#0a0520");
      pg.addColorStop(0.65, "#060312");
      pg.addColorStop(1, "#020009");
      ctx.beginPath();
      ctx.arc(ecx, ECY, PUPIL_R, 0, Math.PI * 2);
      ctx.fillStyle = pg;
      ctx.fill();

      ctx.save();
      ctx.translate(ecx, ECY);
      ctx.rotate(rot * 3.2);
      for (let arm = 0; arm < 3; arm++) {
        const aOff = (arm / 3) * Math.PI * 2;
        ctx.beginPath();
        for (let r = 4; r < PUPIL_R; r += 1.4) {
          const a = aOff + r * 0.2;
          r <= 4 ? ctx.moveTo(r * Math.cos(a), r * Math.sin(a)) : ctx.lineTo(r * Math.cos(a), r * Math.sin(a));
        }
        ctx.strokeStyle = `rgba(120,200,255,${(0.35 + 0.14 * pulse).toFixed(2)})`;
        ctx.lineWidth = 2.2;
        ctx.stroke();
      }
      for (let arm = 0; arm < 5; arm++) {
        const aOff = (arm / 5) * Math.PI * 2;
        ctx.beginPath();
        for (let r = 2; r < PUPIL_R * 0.65; r += 1) {
          const a = aOff + r * 0.32;
          r <= 2 ? ctx.moveTo(r * Math.cos(a), r * Math.sin(a)) : ctx.lineTo(r * Math.cos(a), r * Math.sin(a));
        }
        ctx.strokeStyle = `rgba(180,230,255,${(0.2 + 0.08 * pulse).toFixed(2)})`;
        ctx.lineWidth = 1;
        ctx.stroke();
      }
      ctx.restore();

      data.pupilStars.forEach((s) => {
        if (Math.hypot(s.x - ecx, s.y - ECY) < PUPIL_R - 1) {
          const tw = 0.4 + 0.6 * Math.abs(Math.sin(frame * 0.032 + s.tw));
          ctx.beginPath();
          ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(230,245,255,${(tw * 0.95).toFixed(2)})`;
          ctx.fill();
        }
      });

      const pn = ctx.createRadialGradient(ecx - 14, ECY - 10, 0, ecx, ECY, PUPIL_R);
      pn.addColorStop(0, `rgba(140,55,230,${0.38 + 0.14 * pulse})`);
      pn.addColorStop(0.5, "rgba(60,120,210,0.22)");
      pn.addColorStop(1, "rgba(0,0,0,0)");
      ctx.beginPath();
      ctx.arc(ecx, ECY, PUPIL_R, 0, Math.PI * 2);
      ctx.fillStyle = pn;
      ctx.fill();

      const cg = ctx.createRadialGradient(ecx, ECY, 0, ecx, ECY, 32);
      cg.addColorStop(0, "rgba(255,255,255,1.0)");
      cg.addColorStop(0.18, `rgba(210,248,255,${0.92 + 0.07 * pulse})`);
      cg.addColorStop(0.45, `rgba(90,190,255,${0.65 + 0.22 * pulse})`);
      cg.addColorStop(0.8, `rgba(55,138,221,${0.28 + 0.12 * pulse})`);
      cg.addColorStop(1, "rgba(55,138,221,0)");
      ctx.beginPath();
      ctx.arc(ecx, ECY, 32, 0, Math.PI * 2);
      ctx.fillStyle = cg;
      ctx.fill();
      ctx.beginPath();
      ctx.arc(ecx, ECY, 5.5 + pulse * 3, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(255,255,255,1.0)";
      ctx.fill();

      ctx.save();
      ctx.translate(ecx, ECY);
      ctx.rotate(slowPulse * 0.3);
      for (let i = 0; i < 8; i++) {
        const a = (i / 8) * Math.PI * 2,
          len = i % 2 === 0 ? 22 : 12,
          alp = i % 2 === 0 ? 0.8 + 0.18 * pulse : 0.45 + 0.12 * pulse;
        ctx.strokeStyle = `rgba(230,252,255,${alp})`;
        ctx.lineWidth = i % 2 === 0 ? 2 : 1.2;
        ctx.lineCap = "round";
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(Math.cos(a) * len, Math.sin(a) * len);
        ctx.stroke();
      }
      ctx.restore();

      ctx.beginPath();
      ctx.ellipse(ecx - 34, ECY - 36, 24, 13, -0.5, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(255,255,255,0.32)";
      ctx.fill();
      ctx.beginPath();
      ctx.ellipse(ecx - 27, ECY - 28, 11, 6.5, -0.5, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(255,255,255,0.60)";
      ctx.fill();
      ctx.beginPath();
      ctx.ellipse(ecx + 25, ECY + 23, 8, 4.5, 0.6, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(255,255,255,0.25)";
      ctx.fill();

      if (blink < 0.98) {
        const lidA = (1 - blink) * 0.92;
        ctx.save();
        buildEyePath(elx, erx, TC1, TC2, BC1, BC2);
        ctx.clip();
        const lg = ctx.createLinearGradient(ecx, ECY - 40, ecx, ECY + 40);
        lg.addColorStop(0, `rgba(6,3,20,${lidA})`);
        lg.addColorStop(0.6, `rgba(6,3,20,${lidA * 0.6})`);
        lg.addColorStop(1, "rgba(6,3,20,0)");
        ctx.fillStyle = lg;
        ctx.fillRect(0, 0, W, H);
        ctx.restore();
      }
      ctx.restore();

      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(elx, ECY);
      ctx.bezierCurveTo(TC1.x, TC1.y, TC2.x, TC2.y, erx, ECY);
      ctx.strokeStyle = `rgba(180,240,255,${0.18 + 0.08 * pulse})`;
      ctx.lineWidth = 22;
      ctx.stroke();
      ctx.strokeStyle = `rgba(120,210,255,${0.55 + 0.2 * pulse})`;
      ctx.lineWidth = 9;
      ctx.stroke();
      ctx.strokeStyle = `rgba(230,252,255,${0.95 + 0.05 * pulse})`;
      ctx.lineWidth = 2.8;
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(elx, ECY);
      ctx.bezierCurveTo(BC1.x, BC1.y, BC2.x, BC2.y, erx, ECY);
      ctx.strokeStyle = `rgba(55,138,221,${0.65 + 0.18 * pulse})`;
      ctx.lineWidth = 2.5;
      ctx.stroke();
      ctx.strokeStyle = `rgba(120,200,255,${0.3 + 0.12 * pulse})`;
      ctx.lineWidth = 9;
      ctx.stroke();

      ([elx, erx] as number[]).forEach((cx) => {
        const dg = ctx.createRadialGradient(cx, ECY, 0, cx, ECY, 30);
        dg.addColorStop(0, `rgba(220,250,255,${0.95 + 0.05 * pulse})`);
        dg.addColorStop(0.35, `rgba(120,220,255,${0.6 + 0.18 * pulse})`);
        dg.addColorStop(1, "rgba(120,220,255,0)");
        ctx.beginPath();
        ctx.arc(cx, ECY, 30, 0, Math.PI * 2);
        ctx.fillStyle = dg;
        ctx.fill();
        ctx.beginPath();
        ctx.arc(cx, ECY, 6, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(240,255,255,1.0)";
        ctx.fill();
      });

      for (let t = 0.025; t <= 0.975; t += 0.058) {
        const p = upperPt(t, elx, erx, TC1, TC2),
          d = Math.hypot(p.x - ecx, p.y - ECY) || 1;
        const nx = (p.x - ecx) / d,
          ny = (p.y - ECY) / d;
        const ll = 12 + 9 * Math.sin(t * Math.PI),
          la = 0.42 + 0.2 * Math.sin(t * Math.PI);
        ctx.beginPath();
        ctx.moveTo(p.x, p.y);
        ctx.lineTo(p.x + nx * ll * 2.0, p.y + ny * ll * 2.0);
        ctx.strokeStyle = `rgba(60,160,255,${la * 0.3})`;
        ctx.lineWidth = 6;
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(p.x, p.y);
        ctx.lineTo(p.x + nx * ll * 1.4, p.y + ny * ll * 1.4);
        ctx.strokeStyle = `rgba(160,228,255,${la * 0.6})`;
        ctx.lineWidth = 2.8;
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(p.x, p.y);
        ctx.lineTo(p.x + nx * ll, p.y + ny * ll);
        ctx.strokeStyle = `rgba(220,248,255,${la})`;
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }

      if (dreamFactor > 0.01) {
        const glowA = dreamFactor * Math.max(0, 1 - blink * 4) * (0.5 + 0.5 * pulse);
        if (glowA > 0.005) {
          const dg = ctx.createRadialGradient(ecx, ECY, 0, ecx, ECY, IRIS_R * 2.2);
          dg.addColorStop(0, `rgba(255,190,60,${(glowA * 0.45).toFixed(3)})`);
          dg.addColorStop(0.28, `rgba(55,225,185,${(glowA * 0.3).toFixed(3)})`);
          dg.addColorStop(0.55, `rgba(190,55,255,${(glowA * 0.18).toFixed(3)})`);
          dg.addColorStop(1, "rgba(0,0,0,0)");
          ctx.beginPath();
          ctx.arc(ecx, ECY, IRIS_R * 2.2, 0, Math.PI * 2);
          ctx.fillStyle = dg;
          ctx.fill();
        }
      }
    }

    function draw(): void {
      ctx.clearRect(0, 0, W, H);
      const target = stateRef.current === "open" ? 1.0 : stateRef.current === "half" ? 0.5 : 0.0;
      const dreamTgt = stateRef.current === "dreaming" ? 1.0 : 0.0;
      dreamRef.current += (dreamTgt - dreamRef.current) * 0.012;
      const dreamT = dreamRef.current;
      const remFlutter = dreamT > 0.5 ? Math.sin(frame * 0.22) * 0.06 + Math.sin(frame * 0.37) * 0.03 : 0;
      blinkRef.current += (target + remFlutter - blinkRef.current) * 0.08;
      // Settle detection: dreaming galaxy is up AND lids are closed → report once.
      if (armedRef.current && stateRef.current === "dreaming" && dreamRef.current > 0.9 && blinkRef.current < 0.05) {
        if (!settledRef.current) {
          settledRef.current = true;
          onSettledRef.current?.();
        }
      }
      const blink = blinkRef.current,
        pulse = 0.5 + 0.5 * Math.sin(frame * 0.022);
      const slowPulse = 0.5 + 0.5 * Math.sin(frame * 0.01),
        rot = frame * 0.0052;
      const PUPIL_R = PUPIL_R_BASE + 7 * Math.sin(frame * 0.018);
      drawEye(ECX1, ELX1, ERX1, TC1B1, TC2B1, BC1B1, BC2B1, eye1, pulse, slowPulse, rot, blink, PUPIL_R, dreamT);
      drawEye(ECX2, ELX2, ERX2, TC1B2, TC2B2, BC1B2, BC2B2, eye2, pulse, slowPulse, rot, blink, PUPIL_R, dreamT);
      frame += dtRef.current > 0 ? dtRef.current * 60 : 1;
    }

    drawFnRef.current = draw;
    return () => {
      drawFnRef.current = null;
    };
    // FIX: empty dep array — stateRef handles prop changes without restarting animation
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useVisibleAnimation(
    canvasRef,
    (dt) => {
      if (frozenRef.current) return;
      dtRef.current = dt;
      drawFnRef.current?.();
    },
    { fps: 30 },
  );

  return (
    <canvas
      ref={canvasRef}
      style={{
        display: "block",
        width: "100%",
        height: "auto",
        borderRadius: 8,
      }}
    />
  );
};

/* ================= AFFIRMATION SETTINGS (session slide) ================ */
const AFFIRM_COL = "#3ecb8a";
const AFFIRM_MAX_MIN = 15;

const panelLabel: React.CSSProperties = {
  fontFamily: FONT_MONO,
  fontSize: 10,
  letterSpacing: "1.5px",
  textTransform: "uppercase",
};

// −  10 min  +   (0–15). zeroText replaces "0 min" ("now", "no repeat").
// lockedText: the setting no longer applies — buttons disabled, value replaced.
const MinuteStepper: React.FC<{
  label: string;
  value: number;
  onChange: (min: number) => void;
  zeroText: string;
  lockedText?: string;
}> = ({ label, value, onChange, zeroText, lockedText }) => {
  const t = useSimpleViewText();
  const btn = (delta: number, glyph: string, aria: string) => {
    const next = Math.min(AFFIRM_MAX_MIN, Math.max(0, value + delta));
    const off = next === value || !!lockedText;
    return (
      <button
        type="button"
        onClick={() => onChange(next)}
        disabled={off}
        aria-label={aria}
        style={{
          width: 24,
          height: 24,
          borderRadius: "50%",
          border: "1px solid rgba(62,203,138,0.35)",
          background: "transparent",
          color: AFFIRM_COL,
          opacity: off ? 0.3 : 1,
          cursor: off ? "default" : "pointer",
          fontSize: 14,
          lineHeight: 1,
          padding: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {glyph}
      </button>
    );
  };
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ ...panelLabel, color: "#7f97ad", minWidth: 96 }}>{label}</span>
      {btn(-1, "−", t.lessAria(label))}
      <span
        aria-live="polite"
        style={{
          ...panelLabel,
          letterSpacing: "1px",
          color: lockedText ? AFFIRM_COL : "#d6e2ee",
          fontWeight: lockedText ? 700 : 400,
          minWidth: 66,
          textAlign: "center",
          fontVariantNumeric: "tabular-nums",
          whiteSpace: "nowrap",
        }}
      >
        {lockedText ?? (value === 0 ? zeroText : t.minutes(value))}
      </span>
      {btn(1, "+", t.moreAria(label))}
    </div>
  );
};

// Big countdown to the next affirmation. Ticks on its own so the heavy slide
// doesn't re-render every second.
const AffirmationTimer: React.FC<{
  nextAtRef: React.MutableRefObject<number>;
  running: boolean;
  on: boolean;
  firstPlayed: boolean;
}> = ({ nextAtRef, running, on, firstPlayed }) => {
  const [, tick] = useState(0);
  const t = useSimpleViewText();
  useEffect(() => {
    if (!running || !on) return;
    const id = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [running, on]);

  let big = "--:--";
  let caption = t.stopped;
  let lit = false;
  if (!on) {
    big = t.off;
    caption = "";
  } else if (running) {
    const left = nextAtRef.current - Date.now();
    if (nextAtRef.current > 0 && left >= 0) {
      const secs = Math.ceil(left / 1000);
      big = `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, "0")}`;
      caption = firstPlayed ? t.untilNext : t.untilFirst;
      lit = true;
    } else if (firstPlayed) {
      big = "✓";
      caption = t.done;
      lit = true;
    }
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }} aria-live="off">
      <span
        style={{
          fontFamily: FONT_MONO,
          fontSize: 30,
          fontWeight: 700,
          lineHeight: 1,
          color: lit ? AFFIRM_COL : "#7f97ad",
          opacity: lit ? 1 : 0.6,
          fontVariantNumeric: "tabular-nums",
          textTransform: "uppercase",
        }}
      >
        {big}
      </span>
      {caption && <span style={{ ...panelLabel, fontSize: 9, color: "#7f97ad" }}>{caption}</span>}
    </div>
  );
};

/* ================= HYPNAGOGIC AUDIO HOOK ================================= */
interface HypnagogicAudio {
  startHypnagogic: (volume?: number) => void;
  stopHypnagogic: (fadeSecs?: number) => void;
  isHypnagogicOn: boolean;
}

function useHypnagogicAudio(): HypnagogicAudio {
  const ctxRef = useRef<AudioContext | null>(null);
  const masterRef = useRef<GainNode | null>(null);
  const binRef = useRef<GainNode | null>(null);
  const oscsRef = useRef<OscillatorNode[]>([]);
  const binOscsRef = useRef<OscillatorNode[]>([]);
  const lfoRef = useRef<OscillatorNode | null>(null);
  const [isOn, setIsOn] = useState(false);

  const stopHypnagogic = useCallback((fadeSecs = 2.5) => {
    if (!ctxRef.current) return;
    const ctx = ctxRef.current;
    const now = ctx.currentTime;

    masterRef.current?.gain.cancelScheduledValues(now);
    masterRef.current?.gain.linearRampToValueAtTime(0, now + fadeSecs);
    binRef.current?.gain.cancelScheduledValues(now);
    binRef.current?.gain.linearRampToValueAtTime(0, now + fadeSecs);

    setTimeout(
      () => {
        [...oscsRef.current, ...binOscsRef.current].forEach((o) => {
          try {
            o.stop();
          } catch (_) {}
        });
        try {
          lfoRef.current?.stop();
        } catch (_) {}
        ctx.close().catch(() => {});

        ctxRef.current = null;
        masterRef.current = null;
        binRef.current = null;
        lfoRef.current = null;
        oscsRef.current = [];
        binOscsRef.current = [];
      },
      (fadeSecs + 0.3) * 1000,
    );

    setIsOn(false);
  }, []);

  // ── Hypnagogic audio session config ──────────────────────────────────────
  const HYPNAGOGIC_SESSION = {
    harmonics: [
      [73.4, 0.52],
      [110.1, 0.24],
      [146.8, 0.11],
      [220.2, 0.05],
    ] as [number, number][],
    lfoFreq: 0.042,
    lfoGain: 0.012,
    leftHz: 180,
    rightHz: 183.5,
    binGain: 0.022,
    delayTime: 0.65,
    delayFb: 0.24,
    masterGain: 0.036,
    fadeIn: 4.0,
    binFadeIn: 6.0,
  };

  const startHypnagogic = useCallback((volume = 65) => {
    if (ctxRef.current) return;

    const W = window as typeof window & {
      webkitAudioContext?: typeof AudioContext;
    };
    const Ctx = window.AudioContext ?? W.webkitAudioContext;
    if (!Ctx) {
      console.warn("Web Audio API not supported");
      return;
    }

    const ctx = new Ctx();
    ctxRef.current = ctx;
    const now = ctx.currentTime;
    const S = HYPNAGOGIC_SESSION;
    const gainVal = (volume / 100) * S.masterGain;

    const master = ctx.createGain();
    master.gain.setValueAtTime(0, now);
    master.gain.linearRampToValueAtTime(gainVal, now + S.fadeIn);
    master.connect(ctx.destination);
    masterRef.current = master;

    const delay = ctx.createDelay(1.0);
    const fbG = ctx.createGain();
    delay.delayTime.value = S.delayTime;
    fbG.gain.value = S.delayFb;
    delay.connect(fbG);
    fbG.connect(delay);
    delay.connect(master);

    oscsRef.current = S.harmonics.map(([freq, amp]) => {
      const osc = ctx.createOscillator();
      const og = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      og.gain.value = amp;
      osc.connect(og);
      og.connect(delay);
      og.connect(master);
      osc.start();
      return osc;
    });

    const lfoNode = ctx.createOscillator();
    const lfoG = ctx.createGain();
    lfoNode.frequency.value = S.lfoFreq;
    lfoG.gain.value = S.lfoGain;
    lfoNode.connect(lfoG);
    lfoG.connect(master.gain);
    lfoNode.start();
    lfoRef.current = lfoNode;

    const merger = ctx.createChannelMerger(2);
    const bGain = ctx.createGain();
    bGain.gain.setValueAtTime(0, now);
    bGain.gain.linearRampToValueAtTime(S.binGain, now + S.binFadeIn);
    merger.connect(bGain);
    bGain.connect(ctx.destination);
    binRef.current = bGain;

    const lOsc = ctx.createOscillator(),
      lG = ctx.createGain();
    lOsc.type = "sine";
    lOsc.frequency.value = S.leftHz;
    lG.gain.value = 1;
    lOsc.connect(lG);
    lG.connect(merger, 0, 0);
    lOsc.start();

    const rOsc = ctx.createOscillator(),
      rG = ctx.createGain();
    rOsc.type = "sine";
    rOsc.frequency.value = S.rightHz;
    rG.gain.value = 1;
    rOsc.connect(rG);
    rG.connect(merger, 0, 1);
    rOsc.start();

    binOscsRef.current = [lOsc, rOsc];

    setIsOn(true);
    //console.log("[Hypnagogic] AudioContext state:", ctx.state);
    ctx.resume().then(() => {
      //console.log("[Hypnagogic] Context resumed, oscillators running");
    });

    // Started without a tap (auto-start on entering the Session slide): iOS
    // keeps the context suspended until the next user gesture, so resume then.
    if (ctx.state !== "running") {
      const events = ["pointerdown", "touchend", "keydown"] as const;
      const off = (): void => events.forEach((ev) => window.removeEventListener(ev, onGesture));
      const onGesture = (): void => {
        if (ctxRef.current !== ctx) return off();
        ctx.resume().then(off).catch(() => {});
      };
      events.forEach((ev) => window.addEventListener(ev, onGesture, { passive: true }));
    }
  }, []);

  useEffect(() => {
    return () => {
      stopHypnagogic(0.2);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { startHypnagogic, stopHypnagogic, isHypnagogicOn: isOn };
}

// Session settings live at module level, so they survive leaving the slide
// (which unmounts it) and coming back; a page refresh resets them.
const sessionSettings = { affirmOn: true, affirmStartMin: 10, intervalMin: 5 };

// ── Main View ──────────────────────────────────────────────────────────────────
const SimpleView: React.FC<{ eyeState: EyeState }> = ({ eyeState }) => {
  // Internal selection — initialised from the prop, then driven by clicks
  const [activeState, setActiveState] = useState<EyeState>(eyeState);
  // Deep-sleep freeze: 10 min into a session, stop ALL animation and fade to
  // black. Nobody's watching by then; this stops the phone heating overnight.
  const FREEZE_MS = 15000; // TEST 15s — revert to 10 * 60 * 1000
  const [frozen, setFrozen] = useState(false);
  const [pinned, setPinned] = useState(false); // dreaming pinned, still animating
  const [shownState, setShownState] = useState<EyeState>(eyeState);
  const [labelOp, setLabelOp] = useState<number>(1);
  const { startHypnagogic, stopHypnagogic, isHypnagogicOn } = useHypnagogicAudio();
  const t = useSimpleViewText();

  // Affirmations — when on, the first plays affirmStartMin after the session
  // starts, then every intervalMin (0 = play once, no repeat). Both 0–15 min.
  // The hypnagogic background itself starts immediately.
  const [affirmOn, setAffirmOn] = useState(sessionSettings.affirmOn);
  const affirmOnRef = useRef(sessionSettings.affirmOn);
  affirmOnRef.current = affirmOn;
  const nextAffirmAtRef = useRef<number>(0); // Date.now() of the next cue, 0 = none
  const [affirmStartMin, setAffirmStartMin] = useState<number>(sessionSettings.affirmStartMin);
  const affirmStartMinRef = useRef<number>(sessionSettings.affirmStartMin);
  affirmStartMinRef.current = affirmStartMin;
  const [intervalMin, setIntervalMin] = useState<number>(sessionSettings.intervalMin);
  const intervalSecRef = useRef<number>(sessionSettings.intervalMin * 60);
  const intervalSec = intervalMin * 60;
  useEffect(() => {
    sessionSettings.affirmOn = affirmOn;
    sessionSettings.affirmStartMin = affirmStartMin;
    sessionSettings.intervalMin = intervalMin;
  }, [affirmOn, affirmStartMin, intervalMin]);
  const sessionStartAtRef = useRef<number>(0); // Date.now() when the session started
  const firstAffirmDoneRef = useRef(false);
  const [firstPlayed, setFirstPlayed] = useState(false); // locks "First after"

  // Spoken intro ("Keep your eyes closed…"), INTRO_DELAY_MS after the drone
  // starts. Primed inside the Play tap so iOS allows the delayed play().
  const INTRO_DELAY_MS = 2000;
  const introRef = useRef<HTMLAudioElement | null>(null);
  const introTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stopIntro = useCallback(() => {
    if (introTimerRef.current) { clearTimeout(introTimerRef.current); introTimerRef.current = null; }
    const a = introRef.current;
    if (a) { try { a.pause(); a.currentTime = 0; } catch (_) {} }
  }, []);
  useEffect(() => () => stopIntro(), [stopIntro]); // leaving the slide

  const startSession = useCallback(() => {
    sessionStartAtRef.current = Date.now();
    firstAffirmDoneRef.current = false;
    setFirstPlayed(false);
    startHypnagogic();

    stopIntro();
    const intro = introRef.current ?? localAudio("/audio/intro-session.mp3");
    introRef.current = intro;
    intro.preload = "auto";
    // Unlock: start and pause at once (synchronously), so a slow first load
    // can never pause the real playback below.
    intro.muted = true;
    intro.play().catch(() => {});
    intro.pause();
    intro.currentTime = 0;
    intro.muted = false;
    introTimerRef.current = setTimeout(() => {
      introTimerRef.current = null;
      intro.muted = false;
      intro.currentTime = 0;
      intro.play().catch(() => {});
    }, INTRO_DELAY_MS);
  }, [startHypnagogic, stopIntro]);

  const affirmRef = useRef<HTMLAudioElement | null>(null);
  const affirmSecondRef = useRef<HTMLAudioElement | null>(null);
  const affirmTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasUserGestureUnlockRef = useRef(false);

  const ensureAffirmationAudio = useCallback(() => {
    if (affirmRef.current && affirmSecondRef.current) {
      return [affirmRef.current, affirmSecondRef.current] as const;
    }

    const first = affirmRef.current ?? localAudio("/audio/stay-aware.mp3");
    const second = affirmSecondRef.current ?? localAudio("/audio/stay-conscious.mp3");
    first.preload = "auto";
    second.preload = "auto";
    first.onended = () => {
      // keep affirmRef pinned to "first" (stay-aware) so later cycles still
      // start there — reassigning it here made every cycle after the first
      // replay only "stay-conscious"
      second.currentTime = 0;
      second.play().catch(() => {});
    };
    affirmRef.current = first;
    affirmSecondRef.current = second;
    return [first, second] as const;
  }, []);

  // iOS/Safari only permits audio playback after a real user gesture. We prime
  // the same Audio elements during the first tap/click, then the timer loop can
  // continue without Safari blocking later plays.
  const unlockAffirmationAudio = useCallback(() => {
    const [first, second] = ensureAffirmationAudio();
    hasUserGestureUnlockRef.current = true;
    [first, second].forEach((audio) => {
      const previousVolume = audio.volume;
      audio.volume = 0;
      audio.currentTime = 0;
      audio
        .play()
        .then(() => {
          audio.pause();
          audio.currentTime = 0;
          audio.volume = previousVolume;
        })
        .catch(() => {
          audio.volume = previousVolume;
        });
    });
  }, [ensureAffirmationAudio]);

  const playAffirmations = useCallback(() => {
    try {
      if (!hasUserGestureUnlockRef.current) {
        unlockAffirmationAudio();
        return;
      }

      const [first] = ensureAffirmationAudio();
      first.pause();
      first.currentTime = 0;
      affirmRef.current = first;
      const playResult = first.play();
      if (playResult && typeof playResult.catch === "function") {
        playResult.catch(() => {
          hasUserGestureUnlockRef.current = false;
          unlockAffirmationAudio();
        });
      }
    } catch (_) {}
  }, [ensureAffirmationAudio, unlockAffirmationAudio]);

  const scheduleNextAffirmation = useCallback(() => {
    if (affirmTimerRef.current) {
      clearTimeout(affirmTimerRef.current);
      affirmTimerRef.current = null;
    }
    nextAffirmAtRef.current = 0;
    if (!affirmOnRef.current) return;
    const secs = intervalSecRef.current;
    if (firstAffirmDoneRef.current && secs <= 0) return; // "no repeat"
    // Before the first affirmation, wait until its start time; after it,
    // repeat every interval.
    const wait = firstAffirmDoneRef.current
      ? secs * 1000
      : Math.max(0, sessionStartAtRef.current + affirmStartMinRef.current * 60000 - Date.now());
    nextAffirmAtRef.current = Date.now() + wait;
    affirmTimerRef.current = setTimeout(() => {
      firstAffirmDoneRef.current = true;
      setFirstPlayed(true);
      playAffirmations();
      scheduleNextAffirmation();
    }, wait);
  }, [playAffirmations]);

  const stopAffirmationLoop = useCallback(() => {
    stopIntro();
    nextAffirmAtRef.current = 0;
    if (affirmTimerRef.current) {
      clearTimeout(affirmTimerRef.current);
      affirmTimerRef.current = null;
    }
    if (affirmRef.current) {
      try {
        affirmRef.current.pause();
      } catch (_) {}
    }
  }, [stopIntro]);

  // When a setting changes WHILE playing, reset the wait from now:
  // update the ref, then cancel the pending timer and reschedule with the new value.
  useEffect(() => {
    intervalSecRef.current = intervalSec;
    if (isHypnagogicOn) {
      scheduleNextAffirmation(); // clears the old timeout and starts a fresh one
    }
  }, [intervalSec, affirmStartMin, affirmOn, isHypnagogicOn, scheduleNextAffirmation]);

  // Keep internal selection in sync if the parent prop changes (unless frozen)
  useEffect(() => {
    if (!pinned && !frozen) setActiveState(eyeState);
  }, [eyeState, pinned, frozen]);

  // Energy-save: after FREEZE_MS, pin to the DREAMING state, let the eye
  // animate into it for ~1.5s, then freeze the animation there (stays visible).
  useEffect(() => {
    if (!isHypnagogicOn) {
      setFrozen(false);
      setPinned(false);
      return;
    }
    const t = setTimeout(() => setPinned(true), FREEZE_MS); // pin to dreaming; freeze fires when the eye reports settled
    return () => clearTimeout(t);
  }, [isHypnagogicOn, FREEZE_MS]);

  // Auto-cycle through the eye states every 15s: open → half → closed → dreaming → ↺
  // Stops once frozen (deep-sleep energy-save), so the state + label lock.
  useEffect(() => {
    if (pinned || frozen) return;
    const order: EyeState[] = ["open", "half", "closed", "dreaming"];
    const id = setInterval(() => {
      setActiveState((prev) => {
        const i = order.indexOf(prev);
        return order[(i + 1) % order.length];
      });
    }, 15000);
    return () => clearInterval(id);
  }, [pinned, frozen]);

  // Pin everything to "dreaming" the moment energy-save begins.
  useEffect(() => {
    if (pinned) {
      setActiveState("dreaming");
      setShownState("dreaming");
    }
  }, [pinned]);

  useEffect(() => {
    setLabelOp(0);
    const t = setTimeout(() => {
      setShownState(activeState);
      setLabelOp(1);
    }, 500);
    return () => clearTimeout(t);
  }, [activeState]);

  useEffect(() => {
    const stop = () => {
      if (isHypnagogicOn) stopHypnagogic(0.4);
      stopAffirmationLoop();
    };
    window.addEventListener("yogyism:stop", stop);
    return () => window.removeEventListener("yogyism:stop", stop);
  }, [isHypnagogicOn, stopHypnagogic, stopAffirmationLoop]);

  useEffect(() => {
    const autoStart = () => {
      if (!isHypnagogicOn) {
        unlockAffirmationAudio();
        startSession();
        window.dispatchEvent(new Event("yogyism:play"));
      }
    };
    window.addEventListener("yogyism:autoplaySession", autoStart);
    return () => window.removeEventListener("yogyism:autoplaySession", autoStart);
  }, [isHypnagogicOn, startSession, unlockAffirmationAudio]);

  useEffect(() => {
    const handleGestureUnlock = () => {
      unlockAffirmationAudio();
    };

    window.addEventListener("pointerdown", handleGestureUnlock, { passive: true });
    window.addEventListener("touchstart", handleGestureUnlock, { passive: true });
    window.addEventListener("keydown", handleGestureUnlock, { passive: true });

    return () => {
      window.removeEventListener("pointerdown", handleGestureUnlock);
      window.removeEventListener("touchstart", handleGestureUnlock);
      window.removeEventListener("keydown", handleGestureUnlock);
    };
  }, [unlockAffirmationAudio]);

  const labels: Record<EyeState, { text: string; col: string }> = {
    open: { text: t.captions.open, col: "#4fc3f7" },
    half: { text: t.captions.half, col: "#9b72dd" },
    closed: { text: t.captions.closed, col: "#3ecb8a" },
    dreaming: { text: t.captions.dreaming, col: "#c8a050" },
  };

  const opts: { value: EyeState; label: string }[] = [
    { value: "open", label: t.states.open },
    { value: "half", label: t.states.half },
    { value: "closed", label: t.states.closed },
    { value: "dreaming", label: t.states.dreaming },
  ];

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        minHeight: 0,
        height: "100%", // fill the slide (not 100dvh: the slide is shorter than the window)
        overflowX: "hidden",
        overflowY: "auto",
        background: "#04060f",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        paddingBottom: "clamp(10px,2vh,24px)",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          opacity: frozen ? 0 : 1,
          transition: "opacity 1.2s ease",
        }}
      >
        <SpaceBackground eyeState={activeState} frozen={frozen} />
      </div>

      <div
        style={{
          position: "relative",
          zIndex: 10,
          marginTop: "auto", // bottom-aligned, but scrolls (not clipped) when taller than the slide
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "clamp(10px,1.5vh,18px)",
          width: "clamp(320px,58vw,640px)",
          padding: "0 16px",
        }}
      >
        <DualEyeCanvas eyeState={activeState} frozen={frozen} armed={pinned} onSettled={() => setFrozen(true)} />

        {/* <p style={{
          margin: 0,
          fontFamily: "'Georgia', serif",
          fontStyle: "italic",
          fontSize: "clamp(11px, 1.1vw, 15px)",
          letterSpacing: "0.18em",
          color: labels[shownState].col,
          opacity: labelOp,
          transition: "opacity 0.5s ease, color 0.5s ease",
          userSelect: "none",
          textAlign: "center",
        }}>
          {labels[shownState].text}
        </p> */}

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "clamp(10px,1.6vh,18px)",
            alignItems: "center",
            background: "transparent",
            padding: "clamp(8px,1.5vh,20px) clamp(18px,2vw,45px)",
            paddingBottom: 0,
          }}
        >
          {/* ── Control row: play/pause + spring button ── */}
          <div
            style={{
              display: "flex",
              gap: "clamp(12px,1.5vw,20px)",
              alignItems: "center",
            }}
          >
            {/* Play / Pause toggle */}
            <button
              onClick={() => {
                if (isHypnagogicOn) {
                  stopHypnagogic();
                  stopAffirmationLoop();
                  // stay paused — do not resume the slideshow
                } else {
                  unlockAffirmationAudio();
                  startSession();
                  window.dispatchEvent(new Event("yogyism:play"));
                }
              }}
              aria-label={isHypnagogicOn ? t.stopAria : t.playAria}
              style={{
                width: 48,
                height: 48,
                flexShrink: 0,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: isHypnagogicOn ? "rgba(155,114,221,0.12)" : "rgba(79,195,247,0.16)",
                border: `1px solid ${isHypnagogicOn ? "rgba(155,114,221,0.6)" : "rgba(79,195,247,0.6)"}`,
                color: isHypnagogicOn ? "#9b72dd" : "#4fc3f7",
                cursor: "pointer",
                transition: "all 0.25s ease",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.08)")}
              onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
            >
              {isHypnagogicOn ? (
                <svg width="14" height="14" viewBox="0 0 10 10" fill="currentColor">
                  <rect x="0" y="0" width="10" height="10" rx="1.5" />
                </svg>
              ) : (
                <svg width="14" height="14" viewBox="0 0 9 10" fill="currentColor">
                  <polygon points="0,0 9,5 0,10" />
                </svg>
              )}
            </button>

            {/* Affirmation settings: header with switch; steppers left, big timer right */}
            <div
              role="group"
              aria-label={t.affirmations}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 8,
                padding: "10px 14px",
                borderRadius: 10,
                border: `1px solid ${affirmOn ? "rgba(62,203,138,0.28)" : "rgba(127,151,173,0.2)"}`,
                background: "rgba(4,6,15,0.55)",
                transition: "border-color 0.3s ease",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ ...panelLabel, color: affirmOn ? AFFIRM_COL : "#7f97ad", flex: 1 }}>{t.affirmations}</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={affirmOn}
                  aria-label={t.affirmSwitchAria}
                  onClick={() => setAffirmOn((v) => !v)}
                  style={{
                    position: "relative",
                    flexShrink: 0,
                    width: 34,
                    height: 18,
                    borderRadius: 9,
                    border: `1px solid ${affirmOn ? AFFIRM_COL : "rgba(127,151,173,0.5)"}`,
                    background: affirmOn ? "rgba(62,203,138,0.25)" : "transparent",
                    cursor: "pointer",
                    padding: 0,
                    transition: "all 0.25s ease",
                  }}
                >
                  <span
                    style={{
                      position: "absolute",
                      top: 2,
                      left: affirmOn ? 18 : 2,
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      background: affirmOn ? AFFIRM_COL : "#7f97ad",
                      transition: "left 0.25s ease",
                    }}
                  />
                </button>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "10px 18px" }}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                    opacity: affirmOn ? 1 : 0.4,
                    transition: "opacity 0.3s ease",
                  }}
                >
                  <MinuteStepper
                    label={t.firstAfter}
                    value={affirmStartMin}
                    onChange={setAffirmStartMin}
                    zeroText={t.now}
                    lockedText={firstPlayed ? t.played : undefined}
                  />
                  <MinuteStepper label={t.repeatEvery} value={intervalMin} onChange={setIntervalMin} zeroText={t.noRepeat} />
                </div>
                <div style={{ flex: "1 0 72px", display: "flex", justifyContent: "center" }}>
                  <AffirmationTimer
                    nextAtRef={nextAffirmAtRef}
                    running={isHypnagogicOn}
                    on={affirmOn}
                    firstPlayed={firstPlayed}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* ── State options row ── */}
          <div
            style={{
              display: "flex",
              gap: "clamp(6px,1.5vw,24px)",
              alignItems: "center",
            }}
          >
            {opts.map((opt) => {
              const isActive = activeState === opt.value;
              const col = labels[opt.value].col;
              return (
                <button
                  key={opt.value}
                  onClick={() => setActiveState(opt.value)}
                  style={{
                    color: isActive ? col : "#3d6882",
                    background: isActive ? `${col}1f` : "transparent",
                    border: `1px solid ${isActive ? col : "transparent"}`,
                    borderRadius: 6,
                    cursor: "pointer",
                    fontFamily: FONT_MONO,
                    fontSize: "clamp(7px,0.8vw,10px)",
                    letterSpacing: "1.5px",
                    textTransform: "uppercase",
                    transition: "color 0.4s ease, background 0.3s ease, border-color 0.3s ease",
                    userSelect: "none",
                    padding: "16px",
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) e.currentTarget.style.color = col;
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) e.currentTarget.style.color = "#3d6882";
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimpleView;
